import React, { Component } from 'react';
import { Trophy, Zap, ChevronRight } from 'lucide-react';
import { AchievementData } from '../hooks/useStatsData';
import { CardSkeleton } from './CardSkeleton';
interface CardAchievementsProps {
  loading: boolean;
  data: AchievementData | null;
}
export const CardAchievements: React.FC<CardAchievementsProps> = ({
  loading,
  data
}) => {
  if (loading) return <CardSkeleton data-id="element-20668" />;
  if (!data) return <div className="bg-white dark:bg-gradient-to-b dark:from-[#0f0c29] dark:to-[#302b63] rounded-lg border border-purple-200/50 dark:border-indigo-500/30 p-5 shadow-sm dark:shadow-[0_0_15px_rgba(138,43,226,0.2)] backdrop-blur-sm" data-id="element-20669">
        <div className="flex items-center justify-between mb-4" data-id="element-20670">
          <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-800 dark:text-transparent dark:bg-clip-text dark:bg-gradient-to-r dark:from-amber-300 dark:to-yellow-200" data-id="element-20671">
            <Trophy className="text-amber-500 dark:text-amber-400" size={18} data-id="element-20672" />
            Achievements & Badges
          </h3>
        </div>
        <div className="flex flex-col items-center justify-center py-8" data-id="element-20673">
          <p className="text-gray-500 dark:text-indigo-200/70 text-sm" data-id="element-20674">
            Complete activities to earn achievements and badges.
          </p>
        </div>
      </div>;
  // Helper function to get the right icon component
  const getIconComponent = () => {
    if (data.recent.icon === 'Heart') {
      return <Heart size={20} className="text-white" data-id="element-20675" />;
    } else if (data.next.icon === 'Zap') {
      return <Zap size={20} className="text-white" data-id="element-20676" />;
    } else {
      return <Trophy size={20} className="text-white" data-id="element-20677" />;
    }
  };
  // Helper function to get rarity color
  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common':
        return 'from-gray-400/80 to-gray-500/80';
      case 'rare':
        return 'from-blue-400/80 to-blue-500/80';
      case 'epic':
        return 'from-purple-400/80 to-purple-500/80';
      case 'legendary':
        return 'from-amber-400/80 to-amber-500/80';
      default:
        return 'from-gray-400/80 to-gray-500/80';
    }
  };
  const getSigilGlow = (rarity: string) => {
    switch (rarity) {
      case 'common':
        return 'shadow-[0_0_8px_rgba(156,163,175,0.5)]';
      case 'rare':
        return 'shadow-[0_0_8px_rgba(96,165,250,0.5)]';
      case 'epic':
        return 'shadow-[0_0_8px_rgba(167,139,250,0.5)]';
      case 'legendary':
        return 'shadow-[0_0_8px_rgba(251,191,36,0.5)]';
      default:
        return 'shadow-[0_0_8px_rgba(156,163,175,0.5)]';
    }
  };
  const getNextProgressColor = (rarity: string) => {
    switch (rarity) {
      case 'common':
        return 'bg-gray-400';
      case 'rare':
        return 'bg-blue-400';
      case 'epic':
        return 'bg-purple-400';
      case 'legendary':
        return 'bg-amber-400';
      default:
        return 'bg-gray-400';
    }
  };
  return <div className="bg-white dark:bg-gradient-to-b dark:from-[#0f0c29] dark:to-[#302b63] rounded-lg border border-purple-200/50 dark:border-indigo-500/30 p-5 shadow-sm dark:shadow-[0_0_15px_rgba(138,43,226,0.2)] backdrop-blur-sm relative overflow-hidden" data-id="element-20678">
      <div className="relative" data-id="element-20679">
        <div className="flex items-center justify-between mb-4" data-id="element-20680">
          <h3 className="font-semibold text-lg flex items-center gap-2 text-gray-800 dark:text-transparent dark:bg-clip-text dark:bg-gradient-to-r dark:from-amber-300 dark:to-yellow-200" data-id="element-20681">
            <Trophy className="text-amber-500 dark:text-amber-400" size={18} data-id="element-20682" />
            Achievements & Badges
          </h3>
        </div>
        <div className="flex items-center justify-between mb-4" data-id="element-20683">
          <div className="text-sm" data-id="element-20684">
            <span className="font-medium text-amber-500 dark:text-amber-300" data-id="element-20685">
              {data.unlockedCount}
            </span>
            <span className="text-gray-500 dark:text-indigo-200/70" data-id="element-20686">
              {' '}
              / {data.total} unlocked
            </span>
          </div>
          <div className="flex gap-1" data-id="element-20687">
            <span className="px-2 py-1 bg-gray-50 dark:bg-gray-900/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-500/20 rounded text-xs text-gray-500 dark:text-gray-300" data-id="element-20688">
              {data.rarityBreakdown.common} Common
            </span>
            <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/50 backdrop-blur-sm border border-blue-200/50 dark:border-blue-500/20 text-blue-500 dark:text-blue-300 rounded text-xs" data-id="element-20689">
              {data.rarityBreakdown.rare} Rare
            </span>
            <span className="px-2 py-1 bg-purple-50 dark:bg-purple-900/50 backdrop-blur-sm border border-purple-200/50 dark:border-purple-500/20 text-purple-500 dark:text-purple-300 rounded text-xs" data-id="element-20690">
              {data.rarityBreakdown.epic} Epic
            </span>
            <span className="px-2 py-1 bg-amber-50 dark:bg-amber-900/50 backdrop-blur-sm border border-amber-200/50 dark:border-amber-500/20 text-amber-500 dark:text-amber-300 rounded text-xs" data-id="element-20691">
              {data.rarityBreakdown.legendary} Legendary
            </span>
          </div>
        </div>
        <div className="space-y-5" data-id="element-20692">
          {/* Recent achievement */}
          <div className="flex items-center gap-4" data-id="element-20693">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-br ${getRarityColor(data.recent.rarity)} ${getSigilGlow(data.recent.rarity)} border border-${data.recent.rarity === 'legendary' ? 'amber' : data.recent.rarity === 'epic' ? 'purple' : data.recent.rarity === 'rare' ? 'blue' : 'gray'}-300/50 dark:border-${data.recent.rarity === 'legendary' ? 'amber' : data.recent.rarity === 'epic' ? 'purple' : data.recent.rarity === 'rare' ? 'blue' : 'gray'}-500/30 ritual-sigil-active`} data-id="element-20694">
              {getIconComponent()}
            </div>
            <div data-id="element-20695">
              <div className="flex items-center gap-2" data-id="element-20696">
                <h4 className="font-medium text-sm text-gray-700 dark:text-indigo-200" data-id="element-20697">
                  {data.recent.name}
                </h4>
                <span className={`text-xs px-1.5 py-0.5 rounded-sm bg-gradient-to-r ${getRarityColor(data.recent.rarity)} text-white`} data-id="element-20698">
                  {data.recent.rarity.toUpperCase()}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-indigo-200/70" data-id="element-20699">
                Recently unlocked achievement
              </p>
            </div>
          </div>
          {/* Next achievement */}
          <div className="flex items-center gap-4" data-id="element-20700">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-br ${getRarityColor(data.next.rarity)} opacity-40 border border-${data.next.rarity === 'legendary' ? 'amber' : data.next.rarity === 'epic' ? 'purple' : data.next.rarity === 'rare' ? 'blue' : 'gray'}-200/50 dark:border-${data.next.rarity === 'legendary' ? 'amber' : data.next.rarity === 'epic' ? 'purple' : data.next.rarity === 'rare' ? 'blue' : 'gray'}-500/20`} data-id="element-20701">
              <Zap size={20} className="text-white" data-id="element-20702" />
            </div>
            <div className="flex-1" data-id="element-20703">
              <div className="flex items-center gap-2" data-id="element-20704">
                <h4 className="font-medium text-sm text-gray-600 dark:text-indigo-200/90" data-id="element-20705">
                  {data.next.name}
                </h4>
                <span className={`text-xs px-1.5 py-0.5 rounded-sm bg-gradient-to-r ${getRarityColor(data.next.rarity)} text-white opacity-40`} data-id="element-20706">
                  {data.next.rarity.toUpperCase()}
                </span>
              </div>
              <div className="mt-2" data-id="element-20707">
                <div className="flex items-center justify-between mb-1" data-id="element-20708">
                  <p className="text-xs text-gray-500 dark:text-indigo-200/70" data-id="element-20709">
                    Next achievement to unlock
                  </p>
                  <span className="text-xs text-gray-500 dark:text-indigo-200/70" data-id="element-20710">
                    {data.next.progress}/{data.next.total}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 dark:bg-gray-900/50 backdrop-blur-sm rounded-full overflow-hidden" data-id="element-20711">
                  <div className={`h-full ${getNextProgressColor(data.next.rarity)} glow-bar`} style={{
                  width: `${data.next.progress / data.next.total * 100}%`
                }} data-id="element-20712"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-5 pt-3 border-t border-purple-200/30 dark:border-indigo-500/20" data-id="element-20713">
          <button className="w-full py-2 bg-gradient-to-r from-amber-500 to-amber-600 dark:from-amber-600 dark:to-amber-700 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all flex items-center justify-center gap-1 border border-amber-300/30 dark:border-amber-500/30 shadow-sm dark:shadow-[0_0_10px_rgba(251,191,36,0.2)]" onClick={() => {
          // Navigate to achievements page
          const event = new CustomEvent('navigate', {
            detail: {
              section: 'achievements'
            }
          });
          document.dispatchEvent(event);
        }} data-id="element-20714">
            <span data-id="element-20715">View All Achievements</span>
            <ChevronRight size={16} data-id="element-20716" />
          </button>
        </div>
      </div>
    </div>;
};
// Import Heart icon since it's used in the component
import { Heart } from 'lucide-react';